<template>
  <div class="admin-agents-panel">
    <b-alert v-if="!canManage" variant="warning" show class="mb-0">
      <div class="d-flex align-items-start">
        <feather-icon icon="AlertTriangleIcon" size="18" class="mr-75 flex-shrink-0 mt-25" />
        <div>
          <strong class="d-block mb-25">Permission required</strong>
          <span class="small">You do not have permission to manage voice agents. Contact an administrator if you need access.</span>
        </div>
      </div>
    </b-alert>

    <div v-else>
      <!-- List view -->
      <div v-if="viewMode === 'list'">
        <div class="d-flex flex-wrap justify-content-between align-items-start mb-1">
          <div class="admin-agents-panel__intro mr-1 mb-50">
            <h5 class="admin-agents-panel__title mb-25">AI Agents</h5>
            <p class="text-muted small mb-0">
              An agent is the voice + script that talks to a candidate.
            </p>
          </div>
          <div class="d-flex flex-wrap align-items-center">
            <b-button
              size="sm"
              variant="outline-secondary"
              class="mr-50"
              :disabled="loadingList"
              title="Refresh"
              aria-label="Refresh"
              @click="loadAgents"
            >
              <b-spinner small v-if="loadingList" />
              <feather-icon v-else icon="RefreshCwIcon" size="14" />
            </b-button>
            <b-button
              variant="primary"
              title="Create agent"
              aria-label="Create agent"
              @click="openWizard"
            >
              <feather-icon icon="PlusIcon" size="14" class="mr-25" />
              Create agent
            </b-button>
          </div>
        </div>

        <b-alert v-if="listError" variant="warning" show class="py-75 mb-1">
          <div class="d-flex align-items-start">
            <feather-icon icon="AlertTriangleIcon" size="16" class="mr-50 flex-shrink-0 mt-25" />
            <span class="small">{{ listError }}</span>
          </div>
        </b-alert>

        <p v-if="showEnglishAssignHint" class="text-muted small mb-1">
          Indus may list new agents as Untitled. Assign two existing agents below.
        </p>

        <b-card
          v-if="visibleAgents.length"
          class="admin-agents-assign mb-1"
          body-class="p-1"
        >
          <div class="small font-weight-bold mb-50">Assign English Screening &amp; Drive</div>
          <p class="text-muted small mb-75">
            Label them here even if Indus still says Untitled.
            Refresh drops agents that are no longer on Indus.
          </p>
          <b-row>
            <b-col md="5" class="mb-50">
              <b-form-group
                label="Screening"
                label-class="small font-weight-bold mb-25"
                class="mb-0"
              >
                <b-form-select
                  v-model="assignScreeningId"
                  :options="assignAgentOptions"
                />
              </b-form-group>
            </b-col>
            <b-col md="5" class="mb-50">
              <b-form-group
                label="Drive"
                label-class="small font-weight-bold mb-25"
                class="mb-0"
              >
                <b-form-select
                  v-model="assignDriveId"
                  :options="assignAgentOptions"
                />
              </b-form-group>
            </b-col>
            <b-col md="2" class="mb-50 d-flex align-items-end">
              <b-button
                variant="outline-primary"
                class="w-100"
                :disabled="savingAssign || !canSaveEnglishAssign"
                @click="saveEnglishSlotAssignment"
              >
                <b-spinner small v-if="savingAssign" class="mr-25" />
                Save
              </b-button>
            </b-col>
          </b-row>
        </b-card>

        <b-alert
          v-if="defaultAgentId && filteredAgents.length"
          show
          class="admin-agents-default-note py-75 mb-1"
        >
          <div class="d-flex align-items-start">
            <feather-icon icon="StarIcon" size="16" class="admin-agents-brand-icon mr-50 flex-shrink-0 mt-25" />
            <div>
              <strong class="small">Company default:</strong>
              <span class="small"> {{ defaultAgentLabel }}</span>
              <span class="d-block text-muted small">Used when a job does not have its own agent yet.</span>
            </div>
          </div>
        </b-alert>

        <div
          v-if="visibleAgents.length"
          class="d-flex flex-wrap align-items-center justify-content-between mb-75"
        >
          <b-form-input
            v-model="agentSearch"
            size="sm"
            placeholder="Search by name…"
            class="admin-agents-panel__search"
          />
          <small class="text-muted">{{ filteredAgents.length }} of {{ visibleAgents.length }}</small>
        </div>

        <b-overlay :show="loadingList" rounded spinner-small class="admin-agents-panel__list-wrap">
          <b-row v-if="filteredAgents.length" class="admin-agents-grid">
            <b-col
              v-for="agent in filteredAgents"
              :key="agent.agent_id"
              cols="12"
              md="6"
              xl="4"
              class="mb-1"
            >
              <b-card
                class="admin-agent-card h-100 mb-0"
                :class="{ 'admin-agent-card--default': isDefaultAgent(agent) }"
                body-class="d-flex flex-column p-1"
              >
                <div class="d-flex align-items-start justify-content-between mb-50">
                  <div class="min-w-0 mr-50">
                    <div class="admin-agent-card__name">{{ humanAgentName(agent) }}</div>
                    <div
                      v-if="agentCode(agent)"
                      class="admin-agent-card__code text-muted"
                    >
                      {{ agentCode(agent) }}
                    </div>
                    <div class="admin-agent-card__voice text-muted">
                      {{ agentVoiceLine(agent) }}
                    </div>
                  </div>
                  <b-badge
                    v-if="isDefaultAgent(agent)"
                    class="admin-agents-default-badge flex-shrink-0"
                  >
                    Default
                  </b-badge>
                </div>

                <div class="d-flex flex-wrap align-items-center mb-75">
                  <b-badge
                    v-if="formatCallType(agent.call_type) !== '—'"
                    :class="roleBadgeClass(agent.call_type)"
                    class="text-capitalize mr-50 mb-25"
                  >
                    {{ formatCallType(agent.call_type) }}
                  </b-badge>
                  <b-badge
                    v-if="showStatusBadge(agent.status)"
                    :variant="statusVariant(agent.status)"
                    class="text-capitalize mb-25"
                  >
                    {{ agent.status }}
                  </b-badge>
                </div>

                <small
                  v-if="agent.created_by_name || agent.created_by_emp_id"
                  class="text-muted d-block mb-75"
                >
                  Created by {{ agent.created_by_name || agent.created_by_emp_id }}
                </small>

                <div class="d-flex flex-wrap align-items-center mt-auto pt-25">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    class="mr-50 mb-25"
                    title="Rename and edit this agent"
                    @click="openEditAgent(agent)"
                  >
                    <feather-icon icon="Edit2Icon" size="14" class="mr-25" />
                    Rename
                  </b-button>
                  <b-button
                    v-if="!isDefaultAgent(agent)"
                    size="sm"
                    variant="outline-primary"
                    class="mr-50 mb-25"
                    :disabled="settingDefault === agent.agent_id"
                    @click="setAsDefault(agent)"
                  >
                    <b-spinner
                      v-if="settingDefault === agent.agent_id"
                      small
                      class="mr-25"
                    />
                    <feather-icon v-else icon="StarIcon" size="14" class="mr-25" />
                    Set default
                  </b-button>
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    class="mb-25 ml-auto"
                    :disabled="deletingId === agent.agent_id"
                    title="Delete agent"
                    @click="confirmDelete(agent)"
                  >
                    <b-spinner v-if="deletingId === agent.agent_id" small />
                    <feather-icon v-else icon="Trash2Icon" size="14" />
                  </b-button>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <b-alert
            v-else-if="!loadingList"
            show
            variant="light"
            class="admin-agents-empty mb-0"
          >
            <div class="text-center py-1 px-50">
              <feather-icon icon="UsersIcon" size="22" class="text-muted mb-50" />
              <strong class="d-block mb-25">{{ agentsEmptyTitle }}</strong>
              <p class="text-muted small mb-1">{{ agentsEmptyDescription }}</p>
              <b-button
                v-if="!agentSearch"
                variant="primary"
                @click="openWizard"
              >
                <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                Create agent
              </b-button>
            </div>
          </b-alert>
        </b-overlay>

        <b-modal
          id="admin-agent-edit"
          v-model="showEditModal"
          title="Rename agent"
          size="lg"
          ok-title="Save"
          cancel-title="Cancel"
          ok-variant="primary"
          :ok-disabled="savingEdit || !editForm.name.trim()"
          :busy="savingEdit"
          @ok.prevent="saveAgentEdit"
        >
          <p class="text-muted small mb-1">{{ editSlotHint }}</p>
          <p class="text-muted small mb-1">
            This name is saved in ATS and shows on cards immediately.
            Indus cannot be PATCHed — the portal title may stay Untitled.
          </p>
          <b-form-group
            label="Display name"
            label-class="small font-weight-bold mb-25"
            class="mb-1"
          >
            <b-form-input
              v-model="editForm.name"
              placeholder="e.g. Screening"
            />
          </b-form-group>
          <b-row>
            <b-col md="6" class="mb-1">
              <b-form-group label="Language" label-class="small font-weight-bold mb-25" class="mb-0">
                <b-form-select
                  v-model="editForm.language"
                  :options="languageOptions"
                />
              </b-form-group>
            </b-col>
            <b-col md="6" class="mb-1">
              <b-form-group label="Voice" label-class="small font-weight-bold mb-25" class="mb-0">
                <b-form-select
                  v-model="editForm.voice"
                  :options="editVoiceSelectOptions"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <p class="text-muted small mb-1">
            Default voice is Maya (<code>Indus-en-maya</code> /
            <code>Indus-hi-maya</code>). Language is still
            English / Hindi for Test Call. Set the same pair on Indus Config.
          </p>
          <b-form-group
            label="Opening line"
            label-class="small font-weight-bold mb-25"
            class="mb-1"
          >
            <b-form-textarea
              v-model="editForm.firstMessage"
              rows="4"
              class="agent-prompt-textarea"
            />
          </b-form-group>
          <b-form-group
            label="System prompt"
            label-class="small font-weight-bold mb-25"
            class="mb-0"
          >
            <b-form-textarea
              v-model="editForm.systemPrompt"
              rows="10"
              class="agent-prompt-textarea"
            />
          </b-form-group>
        </b-modal>
      </div>

      <!-- Create wizard -->
      <div v-else>
        <div class="d-flex flex-wrap justify-content-between align-items-start mb-1">
          <div class="admin-agents-panel__intro mr-1 mb-50">
            <h5 class="admin-agents-panel__title mb-25">Create agent</h5>
            <p class="text-muted small mb-50">
              Name them, choose a voice, then review the opening line.
            </p>
            <small class="text-muted d-block mb-25">
              Step {{ wizardStep + 1 }} of {{ wizardSteps.length }} — {{ wizardSteps[wizardStep] }}
            </small>
            <b-progress
              :value="wizardStep + 1"
              :max="wizardSteps.length"
              height="4px"
              class="admin-agents-progress"
              style="max-width: 20rem;"
            />
          </div>
          <b-button size="sm" variant="outline-secondary" @click="cancelWizard">
            <feather-icon icon="ArrowLeftIcon" size="14" class="mr-25" />
            Back to list
          </b-button>
        </div>

        <b-form @submit.prevent="onWizardSubmit">
          <b-tabs
            v-model="wizardStep"
            pills
            nav-class="agent-wizard-tabs d-flex flex-wrap"
            content-class="mt-75"
          >
            <b-tab title="Name">
              <b-row>
                <b-col cols="12" class="mb-1">
                  <b-form-group label="Start from" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-select
                      v-model="wizard.preset"
                      :options="wizardPresetOptions"
                      @change="applyWizardPreset"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6" class="mb-1">
                  <b-form-group label="Name" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-input
                      v-model="wizard.name"
                      placeholder="e.g. Hindi screening — Field roles"
                      required
                    />
                    <b-form-text class="small">
                      Shown in your agent list and when you pick an agent on a job.
                    </b-form-text>
                  </b-form-group>
                </b-col>
                <b-col md="6" class="mb-1">
                  <b-form-group label="Note" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-input
                      v-model="wizard.description"
                      placeholder="Optional — who this agent is for"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="4" class="mb-1">
                  <b-form-group label="Language" label-class="small font-weight-bold mb-25" class="mb-0">
                    <b-form-select
                      v-model="wizard.language"
                      :options="languageOptions"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="8" class="mb-1">
                  <b-form-group label="Voice" label-class="small font-weight-bold mb-25" class="mb-0">
                    <v-select
                      v-model="wizard.voice"
                      :options="voiceOptions"
                      taggable
                      push-tags
                      :clearable="false"
                      placeholder="How the agent should sound"
                      class="agent-voice-select"
                    >
                      <template #selected-option="{ label }">
                        {{ humanVoiceLabel(label) }}
                      </template>
                      <template #option="{ label }">
                        {{ humanVoiceLabel(label) }}
                      </template>
                    </v-select>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-tab>

            <b-tab title="Script">
              <b-form-group label="Opening line" label-class="small font-weight-bold mb-25" class="mb-1">
                <b-form-textarea
                  v-model="wizard.firstMessage"
                  rows="4"
                  class="agent-prompt-textarea"
                />
                <b-form-text class="small">
                  First thing the agent says. The candidate’s name is filled in when you call.
                </b-form-text>
              </b-form-group>

              <b-form-checkbox v-model="wizard.useOrgPromptTemplate" switch class="mb-1">
                Use the company default script
              </b-form-checkbox>

              <div v-if="wizard.useOrgPromptTemplate" class="mb-0">
                <b-alert show variant="light" class="py-75 mb-1 small">
                  Job details (client, role, location) fill in automatically on the job’s AI Calling tab.
                </b-alert>
                <b-row>
                  <b-col md="4" class="mb-1">
                    <b-form-group label="Speaking name" label-class="small font-weight-bold mb-25" class="mb-0">
                      <b-form-input
                        v-model="wizard.promptFields.agent_name"
                        placeholder="e.g. Nikita"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="8" class="mb-1">
                    <b-form-group label="Your company" label-class="small font-weight-bold mb-25" class="mb-0">
                      <b-form-input
                        v-model="wizard.promptFields.company"
                        placeholder="Your staffing company"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>

              <b-form-group
                v-else
                label="What the agent should do"
                label-class="small font-weight-bold mb-25"
                class="mb-0"
              >
                <b-form-textarea
                  v-model="wizard.systemPrompt"
                  rows="8"
                  class="agent-prompt-textarea"
                />
              </b-form-group>
            </b-tab>

            <b-tab title="Review">
              <b-card class="admin-agents-review mb-1" body-class="p-1">
                <dl class="agent-review-dl mb-0">
                  <div class="d-flex justify-content-between py-50 border-bottom">
                    <dt class="mb-0 text-muted small">Name</dt>
                    <dd class="mb-0 small font-weight-bold text-right">{{ wizard.name || '—' }}</dd>
                  </div>
                  <div class="d-flex justify-content-between py-50 border-bottom">
                    <dt class="mb-0 text-muted small">Language / voice</dt>
                    <dd class="mb-0 small font-weight-bold text-right">
                      {{ languageLabel }} · {{ humanVoiceLabel(wizard.voice) }}
                    </dd>
                  </div>
                  <div class="py-50 border-bottom">
                    <dt class="mb-25 text-muted small">Opening line</dt>
                    <dd class="mb-0">
                      <pre class="agent-preview-pre small mb-0">{{ previewFirstMessage }}</pre>
                    </dd>
                  </div>
                  <div class="py-50">
                    <dt class="mb-25 text-muted small">
                      Script
                      <b-button
                        size="sm"
                        variant="link"
                        class="p-0 align-baseline ml-25 admin-agents-link"
                        @click="showPromptPreview = !showPromptPreview"
                      >
                        {{ showPromptPreview ? 'Hide' : 'Show' }}
                      </b-button>
                    </dt>
                    <dd v-if="showPromptPreview" class="mb-0">
                      <pre class="agent-preview-pre small mb-0">{{ previewSystemPrompt }}</pre>
                    </dd>
                    <dd v-else class="mb-0 small text-muted">
                      {{ previewSystemPrompt.length }} characters — click Show to expand
                    </dd>
                  </div>
                </dl>
              </b-card>

              <b-alert v-if="createError" variant="danger" show class="py-75 mb-1">
                {{ createError }}
              </b-alert>

              <b-alert v-if="createdAgentId" variant="success" show class="py-75 mb-1">
                Agent created{{ wizard.name ? `: ${wizard.name}` : '' }}.
                Next, assign it on a job’s AI Calling tab.
                <b-form-checkbox v-model="setDefaultOnCreate" switch class="mt-75 mb-0">
                  Use this as the company default agent
                </b-form-checkbox>
              </b-alert>

              <b-button
                v-if="!createdAgentId"
                variant="primary"
                type="submit"
                :disabled="creating || !canSubmitWizard"
              >
                <b-spinner small v-if="creating" class="mr-25" />
                Create agent
              </b-button>
              <b-button
                v-else
                variant="outline-primary"
                type="button"
                :disabled="savingDefault"
                @click="finishWizard"
              >
                Done
              </b-button>
            </b-tab>
          </b-tabs>
        </b-form>

        <div
          v-if="!createdAgentId"
          class="agent-wizard-footer d-flex flex-wrap align-items-center justify-content-between mt-1 pt-75 border-top"
        >
          <div>
            <b-button
              size="sm"
              variant="outline-secondary"
              class="mr-50"
              :disabled="wizardStep === 0"
              @click="wizardStep -= 1"
            >
              Back
            </b-button>
            <b-button
              v-if="wizardStep < wizardSteps.length - 1"
              size="sm"
              variant="outline-primary"
              :disabled="wizardStep === 0 && !wizard.name.trim()"
              @click="wizardStep += 1"
            >
              Next
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BFormCheckbox,
  BFormText,
  BOverlay,
  BProgress,
  BBadge,
  BSpinner,
  BTabs,
  BTab,
  BRow,
  BCol,
  BModal,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import aiCallingService, {
  excludeDeletedIndusAgents,
  extractLiveIndusAgentsList,
  getAiCallingErrorMessage,
  getDeletedIndusAgentIds,
  isAgentAlreadyGone,
  isAiCallingNotDeployed,
  isDeletedIndusAgent,
  isIndusAgentDeleteSuccess,
  listedIndusAgentId,
  normalizeIndusAgentId,
  parseIndusAgentsListResponse,
  rememberDeletedIndusAgent,
  rememberDeletedIndusAgents,
} from '@/services/ai-calling-service'
import adminConfigService from '@/services/admin-config-service'
import { canManageAiCallingConfig } from '@/utils/admin-settings-permissions'
import {
  DEFAULT_SYSTEM_PROMPT,
  DEFAULT_FIRST_MESSAGE,
  DEFAULT_DRIVE_FIRST_MESSAGE,
  DEFAULT_DRIVE_SYSTEM_PROMPT,
  ENGLISH_SCREENING_AGENT_NAME,
  ENGLISH_DRIVE_AGENT_NAME,
  isStockHinglishPrompt,
  applyPromptVariables,
  defaultOrgPromptFields,
  TRANSCRIPT_LANGUAGE_OPTIONS,
  defaultVoiceForLanguage,
  voiceSelectOptionsForLanguage,
  getAgentVoiceSettings,
  formatAgentVoiceLine,
  normalizeAgentVoiceMap,
  normalizeAgentVoiceEntry,
  humanVoiceLabel,
  voiceMatchesLanguage,
  isBrokenHindiConfigAgent,
  DEFAULT_ENGLISH_VOICE_ID,
} from '@/utils/ai-calling-prompt-defaults'
import {
  canSubmitCreateAgent,
  extractCreatedAgentId,
  buildAdminCreateAgentInput,
} from '@/utils/ai-calling-agent-create'
import { getLanguageOptions } from '@/utils/ai-calling-catalog'
import { parseAiCallingSchemaConfig } from '@/utils/ai-calling-config-schema'

const DEFAULT_WIZARD = () => ({
  name: '',
  description: '',
  preset: 'custom',
  language: '',
  voice: '',
  firstMessage: '',
  useOrgPromptTemplate: true,
  systemPrompt: '',
  orgSystemPromptTemplate: '',
  promptFields: defaultOrgPromptFields(),
  config: null,
})

function looksLikeAgentCode(value) {
  const s = (value || '').toString().trim()
  if (!s) return false
  return /^AGT[_-]/i.test(s) || /^[A-Z]{2,5}_[A-F0-9]{6,}$/i.test(s)
}

export default {
  name: 'AdminAgentsPanel',
  components: {
    BAlert,
    BButton,
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BFormCheckbox,
    BFormText,
    BOverlay,
    BProgress,
    BBadge,
    BSpinner,
    BTabs,
    BTab,
    BRow,
    BCol,
    BModal,
    vSelect,
  },
  props: {
    embedded: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      viewMode: 'list',
      agents: [],
      agentSearch: '',
      loadingList: false,
      listError: '',
      listUnavailable: false,
      defaultAgentId: '',
      adminTtsDefaults: {
        model: 'indus-tts-v1',
        outputFormat: 'mp3',
        speed: 1,
        pitchShift: 0,
        loudnessDb: 0,
        sampleRate: 24000,
      },
      wizardStep: 0,
      wizardSteps: ['Name', 'Script', 'Review'],
      wizard: DEFAULT_WIZARD(),
      showPromptPreview: false,
      creating: false,
      createError: '',
      createdAgentId: '',
      setDefaultOnCreate: true,
      savingDefault: false,
      settingDefault: '',
      deletingId: '',
      deletedAgentIds: [],
      englishAgentsEnsured: false,
      assignScreeningId: '',
      assignDriveId: '',
      assignedScreeningId: '',
      assignedDriveId: '',
      assignedOtherId: '',
      savingAssign: false,
      aiCallingNumbers: { number1: '', number2: '' },
      showEditModal: false,
      savingEdit: false,
      editAgentId: '',
      editForm: {
        name: '',
        language: 'en',
        voice: DEFAULT_ENGLISH_VOICE_ID,
        firstMessage: DEFAULT_FIRST_MESSAGE,
        systemPrompt: DEFAULT_SYSTEM_PROMPT,
      },
      agentVoiceMap: {},
      orgCallingSchema: null,
      orgLanguage: '',
      orgVoice: '',
      orgFirstMessage: DEFAULT_FIRST_MESSAGE,
      orgSystemPrompt: DEFAULT_SYSTEM_PROMPT,
      orgDriveFirstMessage: DEFAULT_DRIVE_FIRST_MESSAGE,
      orgDriveSystemPrompt: DEFAULT_DRIVE_SYSTEM_PROMPT,
      agentLabelOverrides: {},
      agentPromptOverrides: {},
    }
  },
  computed: {
    canManage() {
      return canManageAiCallingConfig(this.$can.bind(this))
    },
    defaultAgentLabel() {
      const id = (this.defaultAgentId || '').toString().trim()
      if (!id) return ''
      const match = this.visibleAgents.find(a => String(a.agent_id) === id)
      return match ? this.humanAgentName(match) : id
    },
    agentsEmptyTitle() {
      if (this.agentSearch) return 'No matches'
      if (this.listUnavailable) return "Couldn't load agents yet"
      return 'No agents yet'
    },
    agentsEmptyDescription() {
      if (this.agentSearch) return 'Try a different search term.'
      if (this.listUnavailable) {
        return "Couldn't load your agents yet. You can still create one."
      }
      return 'Create one for Screening or Drive, then assign it on a job’s AI Calling tab.'
    },
    wizardPresetOptions() {
      return [
        { value: 'custom', text: 'Blank / company default' },
        { value: 'screening_en', text: 'Screening English' },
        { value: 'drive_en', text: 'Drive English' },
      ]
    },
    languageOptions() {
      return [{ value: '', text: 'Choose language…' }, ...getLanguageOptions(TRANSCRIPT_LANGUAGE_OPTIONS)]
    },
    editVoiceSelectOptions() {
      return voiceSelectOptionsForLanguage(this.editForm.language)
    },
    languageLabel() {
      const match = this.languageOptions.find(o => o.value === this.wizard.language)
      return match ? match.text : this.wizard.language
    },
    voiceOptions() {
      return voiceSelectOptionsForLanguage(this.wizard.language).map(o => o.value)
    },
    resolvedSystemPromptTemplate() {
      if (this.wizard.useOrgPromptTemplate) {
        return this.wizard.orgSystemPromptTemplate || ''
      }
      return this.wizard.systemPrompt || ''
    },
    previewSystemPrompt() {
      if (this.wizard.useOrgPromptTemplate) {
        return applyPromptVariables(this.resolvedSystemPromptTemplate, this.wizard.promptFields)
      }
      return this.resolvedSystemPromptTemplate
    },
    previewFirstMessage() {
      const fields = this.wizard.useOrgPromptTemplate
        ? this.wizard.promptFields
        : {}
      return applyPromptVariables(this.wizard.firstMessage, fields)
    },
    canSubmitWizard() {
      return canSubmitCreateAgent({
        ...this.wizard,
        previewSystemPrompt: this.previewSystemPrompt,
        config: this.orgCallingSchema || this.wizard.config,
      })
    },
    visibleAgents() {
      return this.excludeDeletedAgents(this.agents)
    },
    showEnglishAssignHint() {
      return this.visibleAgents.length > 0 && !(this.assignedScreeningId && this.assignedDriveId)
    },
    assignAgentOptions() {
      const opts = [{ value: '', text: 'Pick an agent…' }]
      this.visibleAgents.forEach((agent) => {
        const id = listedIndusAgentId(agent)
        if (!id) return
        opts.push({ value: id, text: `${this.humanAgentName(agent)} · ${id}` })
      })
      return opts
    },
    canSaveEnglishAssign() {
      return !!(this.assignScreeningId || this.assignDriveId)
    },
    editSlotHint() {
      const id = (this.editAgentId || '').toString().trim()
      if (id && id === this.assignedScreeningId) {
        return 'Saves the company Screening opening line and script used when a job has no custom script.'
      }
      if (id && id === this.assignedDriveId) {
        return 'Saves the company Drive opening line and script used when a job has no custom script.'
      }
      return 'Saves a label and script in AI Calling settings for this agent id.'
    },
    filteredAgents() {
      const q = (this.agentSearch || '').trim().toLowerCase()
      if (!q) return this.visibleAgents
      return this.visibleAgents.filter(a => {
        const name = String(a.name || '').toLowerCase()
        const id = listedIndusAgentId(a).toLowerCase()
        const creator = String(a.created_by_name || a.created_by_emp_id || '').toLowerCase()
        const role = this.formatCallType(a.call_type).toLowerCase()
        return name.includes(q) || id.includes(q) || creator.includes(q) || role.includes(q)
      })
    },
  },
  watch: {
    'wizard.language'(lang) {
      if (this.wizard.voice && !voiceMatchesLanguage(this.wizard.voice, lang)) {
        this.wizard.voice = ''
      }
    },
    'editForm.language'(lang) {
      if (this.editForm.voice && !voiceMatchesLanguage(this.editForm.voice, lang)) {
        this.editForm.voice = ''
      }
    },
  },
  created() {
    // Mounted only when Admin Settings → Agents is selected (v-if). List is this screen's job.
    if (this.canManage) {
      this.deletedAgentIds = getDeletedIndusAgentIds()
      this.loadAdminTemplates().then(() => {
        this.agents = this.excludeDeletedAgents(this.agents)
        this.clearTombstonedSlots()
      })
      this.loadAgents()
    }
  },
  methods: {
    looksLikeAgentCode,
    openEditAgent(agent) {
      const id = (agent && agent.agent_id ? agent.agent_id : '').toString().trim()
      if (!id) return
      this.editAgentId = id
      this.editForm.name = this.humanAgentName(agent)
      const voicePair = getAgentVoiceSettings(this.agentVoiceMap, id, {
        agentName: this.editForm.name,
        assignedScreeningId: this.assignedScreeningId,
        assignedDriveId: this.assignedDriveId,
      })
      this.editForm.language = voicePair.language
      this.editForm.voice = voicePair.voice
      const override = this.agentPromptOverrides[id] || {}
      if (id === this.assignedScreeningId) {
        this.editForm.firstMessage = this.orgFirstMessage || DEFAULT_FIRST_MESSAGE
        this.editForm.systemPrompt = this.orgSystemPrompt || DEFAULT_SYSTEM_PROMPT
      } else if (id === this.assignedDriveId) {
        this.editForm.firstMessage = this.orgDriveFirstMessage || DEFAULT_DRIVE_FIRST_MESSAGE
        this.editForm.systemPrompt = this.orgDriveSystemPrompt || DEFAULT_DRIVE_SYSTEM_PROMPT
      } else {
        this.editForm.firstMessage = override.firstMessage || DEFAULT_FIRST_MESSAGE
        this.editForm.systemPrompt = override.systemPrompt || DEFAULT_SYSTEM_PROMPT
      }
      this.showEditModal = true
    },
    async saveAgentEdit() {
      if (this.savingEdit || !this.editForm.name.trim()) return
      this.savingEdit = true
      try {
        const cfgRes = await adminConfigService.getAiCallingConfig()
        const existing = cfgRes && (cfgRes.data || cfgRes)
        const base = existing && typeof existing === 'object' && !Array.isArray(existing)
          ? (existing.data && typeof existing.data === 'object' ? existing.data : existing)
          : {}
        const id = this.editAgentId
        const name = this.editForm.name.trim()
        const firstMessage = (this.editForm.firstMessage || '').trim()
        const systemPrompt = (this.editForm.systemPrompt || '').trim()
        const labels = {
          ...(base.agentLabelOverrides || base.agent_label_overrides || this.agentLabelOverrides || {}),
        }
        labels[id] = name
        const prompts = {
          ...(base.agentPromptOverrides || base.agent_prompt_overrides || this.agentPromptOverrides || {}),
        }
        prompts[id] = { firstMessage, systemPrompt }
        const voices = {
          ...(base.agentVoiceMap || base.agent_voice_map || this.agentVoiceMap || {}),
        }
        voices[id] = normalizeAgentVoiceEntry({
          language: this.editForm.language,
          voice: this.editForm.voice,
        }, { agentId: id, agentName: name })
        const patch = {
          ...base,
          agentLabelOverrides: labels,
          agent_label_overrides: labels,
          agentPromptOverrides: prompts,
          agent_prompt_overrides: prompts,
          agentVoiceMap: voices,
          agent_voice_map: voices,
        }
        if (id === this.assignedScreeningId) {
          patch.firstMessage = firstMessage
          patch.defaultFirstMessage = firstMessage
          patch.systemPrompt = systemPrompt
          patch.defaultSystemPrompt = systemPrompt
        }
        if (id === this.assignedDriveId) {
          patch.defaultDriveScript = firstMessage
          patch.default_drive_script = firstMessage
          patch.defaultDriveSystemPrompt = systemPrompt
          patch.default_drive_system_prompt = systemPrompt
        }
        await adminConfigService.saveAiCallingConfig(patch)
        this.agentLabelOverrides = { ...labels }
        this.agentPromptOverrides = prompts
        this.agentVoiceMap = voices
        const listed = this.agents.find(a => String(a.agent_id) === id)
        if (listed) listed.name = name
        if (id === this.assignedScreeningId) {
          this.orgFirstMessage = firstMessage
          this.orgSystemPrompt = systemPrompt
        }
        if (id === this.assignedDriveId) {
          this.orgDriveFirstMessage = firstMessage
          this.orgDriveSystemPrompt = systemPrompt
        }
        this.showEditModal = false
        this.$bvToast.toast(
          `Renamed to “${name}”. ATS cards update now. Indus portal title may still say Untitled.`,
          { title: 'Agents', variant: 'success', solid: true, autoHideDelay: 8000 },
        )
      } catch (e) {
        this.$bvToast.toast('Could not save the org script.', {
          title: 'Agents',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.savingEdit = false
      }
    },
    async saveEnglishSlotAssignment() {
      if (!this.canSaveEnglishAssign || this.savingAssign) return
      this.savingAssign = true
      try {
        await this.assignEnglishAgentsToConfig({
          screeningId: this.assignScreeningId,
          driveId: this.assignDriveId,
        })
        this.assignedScreeningId = this.assignScreeningId
        this.assignedDriveId = this.assignDriveId
        this.$bvToast.toast(
          'Screening and Drive slots saved. Indus titles may stay Untitled.',
          { title: 'Agents', variant: 'success', solid: true },
        )
      } catch (e) {
        this.$bvToast.toast('Could not save the assignment.', {
          title: 'Agents',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.savingAssign = false
      }
    },
    humanVoiceLabel,
    agentVoiceLine(agent) {
      const id = (agent && agent.agent_id ? agent.agent_id : '').toString().trim()
      return formatAgentVoiceLine(getAgentVoiceSettings(this.agentVoiceMap, id, {
        agentName: this.humanAgentName(agent),
        assignedScreeningId: this.assignedScreeningId,
        assignedDriveId: this.assignedDriveId,
      }))
    },
    humanAgentName(agent) {
      const id = (agent && agent.agent_id ? agent.agent_id : '').toString().trim()
      const override = id && this.agentLabelOverrides[id]
        ? String(this.agentLabelOverrides[id]).trim()
        : ''
      if (override) return override
      if (id && id === this.assignedScreeningId) return 'Screening'
      if (id && id === this.assignedDriveId) return 'Drive'
      const name = (agent && agent.name ? agent.name : '').toString().trim()
      if (name && !looksLikeAgentCode(name) && name !== id && !/^untitled/i.test(name)) {
        return name
      }
      const role = this.formatCallType(agent && agent.call_type)
      if (role && role !== '—') return `${role} agent`
      return 'Untitled agent'
    },
    agentCode(agent) {
      const id = (agent && agent.agent_id ? agent.agent_id : '').toString().trim()
      return looksLikeAgentCode(id) ? id : ''
    },
    isDefaultAgent(agent) {
      const id = (this.defaultAgentId || '').toString().trim()
      return !!(id && agent && String(agent.agent_id) === id)
    },
    showStatusBadge(status) {
      const s = String(status || '').toLowerCase()
      return s === 'error' || s === 'failed'
    },
    roleBadgeClass(callType) {
      const role = this.formatCallType(callType)
      if (role === 'Screening') return 'admin-agents-role-badge admin-agents-role-badge--screening'
      if (role === 'Drive') return 'admin-agents-role-badge admin-agents-role-badge--drive'
      return 'admin-agents-role-badge'
    },
    statusVariant(status) {
      const s = String(status || '').toLowerCase()
      if (s === 'active' || s === 'ready' || s === 'ok') return 'light-success'
      if (s === 'error' || s === 'failed') return 'light-danger'
      return 'light-secondary'
    },
    formatCallType(val) {
      const t = String(val || '').trim()
      if (!t) return '—'
      if (t === 'drive' || t === 'follow_up') return 'Drive'
      if (t === 'fresh') return 'Screening'
      return t.replace(/_/g, ' ')
    },
    normalizeAgents(raw) {
      const list = extractLiveIndusAgentsList(raw)
      return list.map(a => {
        if (typeof a === 'string') {
          return { agent_id: a, name: a, status: 'unknown' }
        }
        return {
          agent_id: a.agent_id || a.agentId || a.id || '',
          name: a.name || a.agent_name || a.title || '',
          status: a.status || a.state || 'unknown',
          is_default: !!(a.is_default || a.isDefault),
          updated_at: a.updated_at || a.updatedAt || a.modified_at || '',
          created_by_emp_id: a.created_by_emp_id || a.createdByEmpId || '',
          created_by_name: a.created_by_name || a.createdByName || '',
          prompt_source: a.prompt_source || a.promptSource || '',
          call_type: a.call_type || a.callType || '',
        }
      }).filter(a => a.agent_id)
    },
    async loadAdminTemplates() {
      try {
        const res = await adminConfigService.getAiCallingConfig()
        if (res && (res.status === 'OK' || res.data)) {
          const data = res.data && typeof res.data === 'object' ? res.data : res
          this.defaultAgentId = (data.agentId || data.agent_id || '').toString().trim()
          this.assignedScreeningId = (
            data.agentIdFresh || data.agent_id_fresh || ''
          ).toString().trim()
          this.assignedDriveId = (
            data.agentIdDrive || data.agent_id_drive || ''
          ).toString().trim()
          this.assignedOtherId = (
            data.agentIdOther || data.agent_id_other || ''
          ).toString().trim()
          this.assignScreeningId = this.assignedScreeningId
          this.assignDriveId = this.assignedDriveId
          const systemPrompt = (
            data.systemPrompt || data.defaultSystemPrompt || data.system_prompt || ''
          ).toString()
          const firstMessage = (
            data.firstMessage || data.defaultFirstMessage || data.first_message || ''
          ).toString()
          if (systemPrompt.trim() && !isStockHinglishPrompt(systemPrompt)) {
            this.wizard.orgSystemPromptTemplate = systemPrompt
          }
          if (firstMessage.trim() && !isStockHinglishPrompt(firstMessage)) {
            this.wizard.firstMessage = firstMessage
          }
          const agentName = (
            data.agentName || data.agent_name || data.defaultAgentName || ''
          ).toString().trim()
          const companyName = (
            data.companyName || data.company_name || data.company || ''
          ).toString().trim()
          this.wizard.promptFields = defaultOrgPromptFields({
            ...(agentName ? { agent_name: agentName } : {}),
            ...(companyName ? { company: companyName } : {}),
          })
          this.adminTtsDefaults = {
            model: data.model || 'indus-tts-v1',
            outputFormat: data.outputFormat || data.output_format || 'mp3',
            speed: data.speed != null ? Number(data.speed) : 1,
            pitchShift: data.pitchShift != null ? Number(data.pitchShift)
              : (data.pitch_shift != null ? Number(data.pitch_shift) : 0),
            loudnessDb: data.loudnessDb != null ? Number(data.loudnessDb)
              : (data.loudness_db != null ? Number(data.loudness_db) : 0),
            sampleRate: data.sampleRate || data.sample_rate || 24000,
          }
          const numbers = Array.isArray(data.numbers) ? data.numbers : []
          this.aiCallingNumbers = {
            number1: (data.number1 || numbers[0] || '').toString().trim(),
            number2: (data.number2 || numbers[1] || '').toString().trim(),
          }
          this.orgFirstMessage = firstMessage.trim() && !isStockHinglishPrompt(firstMessage)
            ? firstMessage
            : DEFAULT_FIRST_MESSAGE
          this.orgSystemPrompt = systemPrompt.trim() && !isStockHinglishPrompt(systemPrompt)
            ? systemPrompt
            : DEFAULT_SYSTEM_PROMPT
          const driveFirst = (
            data.defaultDriveScript || data.default_drive_script || ''
          ).toString()
          const driveSys = (
            data.defaultDriveSystemPrompt || data.default_drive_system_prompt || ''
          ).toString()
          this.orgDriveFirstMessage = driveFirst.trim() && !isStockHinglishPrompt(driveFirst)
            ? driveFirst
            : DEFAULT_DRIVE_FIRST_MESSAGE
          this.orgDriveSystemPrompt = driveSys.trim() && !isStockHinglishPrompt(driveSys)
            ? driveSys
            : DEFAULT_DRIVE_SYSTEM_PROMPT
          const deletedFromConfig = data.deletedAgentIds || data.deleted_agent_ids || []
          if (Array.isArray(deletedFromConfig) && deletedFromConfig.length) {
            rememberDeletedIndusAgents(deletedFromConfig)
            this.deletedAgentIds = getDeletedIndusAgentIds()
          }
          this.clearTombstonedSlots()
          const labels = data.agentLabelOverrides || data.agent_label_overrides || {}
          this.agentLabelOverrides = labels && typeof labels === 'object' && !Array.isArray(labels)
            ? { ...labels }
            : {}
          const prompts = data.agentPromptOverrides || data.agent_prompt_overrides || {}
          this.agentPromptOverrides = prompts && typeof prompts === 'object' && !Array.isArray(prompts)
            ? { ...prompts }
            : {}
          this.agentVoiceMap = normalizeAgentVoiceMap(
            data.agentVoiceMap || data.agent_voice_map || {},
          )
          const schema = parseAiCallingSchemaConfig(data)
          this.orgCallingSchema = {
            ...schema,
            screeningInfields: schema.screeningInfields,
            driveInfields: schema.driveInfields,
            screeningOutcomes: schema.screeningOutcomes,
            driveOutcomes: schema.driveOutcomes,
            infieldAliases: schema.infieldAliases,
            requiredInfields: schema.requiredInfields,
            language: schema.language,
            voice: schema.voice,
          }
          this.orgLanguage = schema.language
          this.orgVoice = schema.voice
          if (!this.wizard.language && schema.language) this.wizard.language = schema.language
          if (!this.wizard.voice && schema.voice) this.wizard.voice = schema.voice
        }
      } catch (e) {
        // keep defaults
      }
    },
    applyDefaultFromAgents(agents) {
      if (this.defaultAgentId) return
      const def = (agents || []).find(a => a && a.is_default && a.agent_id)
      if (def) this.defaultAgentId = def.agent_id
    },
    async loadAgents() {
      const reqId = (this._agentsLoadSeq = (this._agentsLoadSeq || 0) + 1)
      this.loadingList = true
      this.listError = ''
      this.listUnavailable = false
      try {
        const res = await aiCallingService.listIndusAgents()
        const parsed = parseIndusAgentsListResponse(res)
        const agents = parsed.ok
          ? this.normalizeAgents(parsed.agents)
          : this.normalizeAgents(res)
        if (reqId !== this._agentsLoadSeq) return
        const visible = this.excludeDeletedAgents(agents)

        this.agents = visible
        this.listUnavailable = !parsed.ok && !visible.length
        if (visible.length) {
          this.applyDefaultFromAgents(visible)
        }
        if (this.canManage) {
          await this.pruneMissingAssignments(visible)
        }
        if (reqId === this._agentsLoadSeq) {
          this.agents = this.excludeDeletedAgents(this.agents)
        }
      } catch (e) {
        if (reqId === this._agentsLoadSeq) {
          this.agents = []
          this.listUnavailable = true
        }
        // eslint-disable-next-line no-console
        console.error('[AdminAgentsPanel] agents list failed', e)
      } finally {
        if (reqId === this._agentsLoadSeq) this.loadingList = false
      }
    },
    deletedAgentIdSet() {
      return new Set(
        getDeletedIndusAgentIds()
          .concat(this.deletedAgentIds || [])
          .map(id => normalizeIndusAgentId(id))
          .filter(Boolean)
          .map(id => id.toUpperCase()),
      )
    },
    excludeDeletedAgents(agents = []) {
      const hidden = this.deletedAgentIdSet()
      const filtered = excludeDeletedIndusAgents(agents)
      if (!hidden.size) return filtered
      return filtered.filter(a => !hidden.has(listedIndusAgentId(a).toUpperCase()))
    },
    rememberDeletedAgent(agentId) {
      const id = normalizeIndusAgentId(agentId)
      if (!id) return
      rememberDeletedIndusAgent(id)
      this.deletedAgentIds = getDeletedIndusAgentIds()
      this.agents = this.excludeDeletedAgents(this.agents)
    },
    clearTombstonedSlots() {
      const clearIfGone = (current) => {
        const id = normalizeIndusAgentId(current)
        return id && isDeletedIndusAgent(id) ? '' : id
      }
      this.assignScreeningId = clearIfGone(this.assignScreeningId)
      this.assignDriveId = clearIfGone(this.assignDriveId)
      this.assignedScreeningId = clearIfGone(this.assignedScreeningId)
      this.assignedDriveId = clearIfGone(this.assignedDriveId)
      this.assignedOtherId = clearIfGone(this.assignedOtherId)
      this.defaultAgentId = clearIfGone(this.defaultAgentId)
    },
    liveAgentIdSet(agents = this.agents) {
      return new Set(
        this.excludeDeletedAgents(agents)
          .map(a => listedIndusAgentId(a).toUpperCase())
          .filter(Boolean),
      )
    },
    keepMapsForLiveIds(map, liveIds) {
      const src = map && typeof map === 'object' && !Array.isArray(map) ? map : {}
      const next = {}
      Object.keys(src).forEach((id) => {
        if (liveIds.has(normalizeIndusAgentId(id).toUpperCase())) next[id] = src[id]
      })
      return next
    },
    async pruneMissingAssignments(liveAgents, { forceSave = false } = {}) {
      const liveIds = this.liveAgentIdSet(liveAgents)
      this.agentLabelOverrides = this.keepMapsForLiveIds(this.agentLabelOverrides, liveIds)
      this.agentPromptOverrides = this.keepMapsForLiveIds(this.agentPromptOverrides, liveIds)
      this.agentVoiceMap = this.keepMapsForLiveIds(this.agentVoiceMap, liveIds)

      const screeningGone = !!(this.assignedScreeningId && !liveIds.has(this.assignedScreeningId.toUpperCase()))
      const driveGone = !!(this.assignedDriveId && !liveIds.has(this.assignedDriveId.toUpperCase()))
      const otherGone = !!(this.assignedOtherId && !liveIds.has(this.assignedOtherId.toUpperCase()))
      const defaultGone = !!(this.defaultAgentId && !liveIds.has(this.defaultAgentId.toUpperCase()))
      if (!forceSave && !screeningGone && !driveGone && !otherGone && !defaultGone) return

      try {
        const cfgRes = await adminConfigService.getAiCallingConfig()
        const existing = cfgRes && (cfgRes.data || cfgRes)
        const base = existing && typeof existing === 'object' && !Array.isArray(existing)
          ? (existing.data && typeof existing.data === 'object' ? existing.data : existing)
          : {}
        const deletedIds = getDeletedIndusAgentIds()
        const patch = {
          ...base,
          agentLabelOverrides: this.agentLabelOverrides,
          agent_label_overrides: this.agentLabelOverrides,
          agentPromptOverrides: this.agentPromptOverrides,
          agent_prompt_overrides: this.agentPromptOverrides,
          agentVoiceMap: this.agentVoiceMap,
          agent_voice_map: this.agentVoiceMap,
          deletedAgentIds: deletedIds,
          deleted_agent_ids: deletedIds,
        }
        if (screeningGone) {
          patch.agent_id_fresh = ''
          patch.agentIdFresh = ''
          this.assignedScreeningId = ''
          this.assignScreeningId = ''
        }
        if (driveGone) {
          patch.agent_id_drive = ''
          patch.agentIdDrive = ''
          this.assignedDriveId = ''
          this.assignDriveId = ''
        }
        if (otherGone) {
          patch.agent_id_other = ''
          patch.agentIdOther = ''
          this.assignedOtherId = ''
        }
        if (defaultGone) {
          patch.agent_id = ''
          patch.agentId = ''
          this.defaultAgentId = ''
        }
        await adminConfigService.saveAiCallingConfig(patch)
      } catch (e) {
        this.assignedScreeningId = screeningGone ? '' : this.assignedScreeningId
        this.assignScreeningId = screeningGone ? '' : this.assignScreeningId
        this.assignedDriveId = driveGone ? '' : this.assignedDriveId
        this.assignDriveId = driveGone ? '' : this.assignDriveId
        this.assignedOtherId = otherGone ? '' : this.assignedOtherId
        this.defaultAgentId = defaultGone ? '' : this.defaultAgentId
      }
    },
    async ensureEnglishOrgAgentsAssigned() {
      if (this.englishAgentsEnsured) return
      this.englishAgentsEnsured = true
      try {
        const result = await aiCallingService.ensureEnglishOrgAgents()
        if (result && result.created && result.created.length) {
          const res = await aiCallingService.listIndusAgents()
          const parsed = parseIndusAgentsListResponse(res)
          this.agents = this.excludeDeletedAgents(
            this.normalizeAgents(parsed.ok ? parsed.agents : res),
          )
        }
        await this.assignEnglishAgentsToConfig(result)
      } catch (e) {
        // Keep the flag so a failed lookup does not retry-loop on every tab open.
      }
    },
    applyWizardPreset() {
      const preset = this.wizard.preset
      this.wizard.language = preset === 'custom' ? (this.orgLanguage || '') : 'en'
      this.wizard.voice = this.orgVoice || ''
      this.wizard.useOrgPromptTemplate = false
      if (preset === 'screening_en') {
        this.wizard.name = ENGLISH_SCREENING_AGENT_NAME
        this.wizard.description = 'English screening calls for fresh candidates'
        this.wizard.firstMessage = DEFAULT_FIRST_MESSAGE
        this.wizard.systemPrompt = DEFAULT_SYSTEM_PROMPT
        this.wizard.orgSystemPromptTemplate = DEFAULT_SYSTEM_PROMPT
        return
      }
      if (preset === 'drive_en') {
        this.wizard.name = ENGLISH_DRIVE_AGENT_NAME
        this.wizard.description = 'English walk-in / hiring drive calls'
        this.wizard.firstMessage = DEFAULT_DRIVE_FIRST_MESSAGE
        this.wizard.systemPrompt = DEFAULT_DRIVE_SYSTEM_PROMPT
        this.wizard.orgSystemPromptTemplate = DEFAULT_DRIVE_SYSTEM_PROMPT
        return
      }
      this.wizard.useOrgPromptTemplate = true
    },
    async assignEnglishAgentsToConfig(result) {
      if (!result || (!result.screeningId && !result.driveId)) return
      const screeningId = isBrokenHindiConfigAgent(result.screeningId)
        ? ''
        : String(result.screeningId || '').trim()
      const driveId = isBrokenHindiConfigAgent(result.driveId)
        ? ''
        : String(result.driveId || '').trim()
      if (!screeningId && !driveId) return
      const cfgRes = await adminConfigService.getAiCallingConfig()
      const existing = cfgRes && (cfgRes.data || cfgRes)
      const base = existing && typeof existing === 'object' && !Array.isArray(existing)
        ? (existing.data && typeof existing.data === 'object' ? existing.data : existing)
        : {}
      const previousFresh = (
        base.agent_id_fresh || base.agentIdFresh || base.agent_id || base.agentId || ''
      ).toString().trim()
      const patch = { ...base }
      let changed = false
      if (screeningId && previousFresh !== screeningId) {
        if (
          previousFresh
          && previousFresh !== screeningId
          && !isBrokenHindiConfigAgent(previousFresh)
          && !patch.agent_id_other
          && !patch.agentIdOther
        ) {
          patch.agent_id_other = previousFresh
          patch.agentIdOther = previousFresh
        }
        patch.agent_id_fresh = screeningId
        patch.agentIdFresh = screeningId
        changed = true
      }
      if (driveId && (base.agent_id_drive || base.agentIdDrive) !== driveId) {
        patch.agent_id_drive = driveId
        patch.agentIdDrive = driveId
        changed = true
      }
      if (isStockHinglishPrompt(patch.firstMessage || patch.defaultFirstMessage)) {
        patch.firstMessage = DEFAULT_FIRST_MESSAGE
        patch.defaultFirstMessage = DEFAULT_FIRST_MESSAGE
        changed = true
      }
      if (isStockHinglishPrompt(patch.systemPrompt || patch.defaultSystemPrompt)) {
        patch.systemPrompt = DEFAULT_SYSTEM_PROMPT
        patch.defaultSystemPrompt = DEFAULT_SYSTEM_PROMPT
        changed = true
      }
      if (!patch.defaultDriveScript || isStockHinglishPrompt(patch.defaultDriveScript)) {
        patch.defaultDriveScript = DEFAULT_DRIVE_FIRST_MESSAGE
        changed = true
      }
      const voices = {
        ...(base.agentVoiceMap || base.agent_voice_map || this.agentVoiceMap || {}),
      }
      const labels = {
        ...(base.agentLabelOverrides || base.agent_label_overrides || this.agentLabelOverrides || {}),
      }
      if (screeningId) {
        voices[screeningId] = normalizeAgentVoiceEntry({
          language: 'en',
          voice: DEFAULT_ENGLISH_VOICE_ID,
        }, { agentId: screeningId, agentName: ENGLISH_SCREENING_AGENT_NAME })
        labels[screeningId] = ENGLISH_SCREENING_AGENT_NAME
        changed = true
      }
      if (driveId) {
        voices[driveId] = normalizeAgentVoiceEntry({
          language: 'en',
          voice: DEFAULT_ENGLISH_VOICE_ID,
        }, { agentId: driveId, agentName: ENGLISH_DRIVE_AGENT_NAME })
        labels[driveId] = ENGLISH_DRIVE_AGENT_NAME
        changed = true
      }
      patch.agentVoiceMap = voices
      patch.agent_voice_map = voices
      patch.agentLabelOverrides = labels
      patch.agent_label_overrides = labels
      if (changed) {
        await adminConfigService.saveAiCallingConfig(patch)
        this.agentVoiceMap = voices
        this.agentLabelOverrides = labels
        if (screeningId) this.assignedScreeningId = screeningId
        if (driveId) this.assignedDriveId = driveId
      }
    },
    async persistCreatedAgentVoice(agentId, { language, voice, name } = {}) {
      const id = String(agentId || '').trim()
      if (!id) return
      try {
        const cfgRes = await adminConfigService.getAiCallingConfig()
        const existing = cfgRes && (cfgRes.data || cfgRes)
        const base = existing && typeof existing === 'object' && !Array.isArray(existing)
          ? (existing.data && typeof existing.data === 'object' ? existing.data : existing)
          : {}
        const voices = {
          ...(base.agentVoiceMap || base.agent_voice_map || this.agentVoiceMap || {}),
        }
        voices[id] = normalizeAgentVoiceEntry({ language, voice }, { agentId: id, agentName: name })
        const labels = {
          ...(base.agentLabelOverrides || base.agent_label_overrides || this.agentLabelOverrides || {}),
        }
        if (name) labels[id] = String(name).trim()
        await adminConfigService.saveAiCallingConfig({
          ...base,
          agentVoiceMap: voices,
          agent_voice_map: voices,
          agentLabelOverrides: labels,
          agent_label_overrides: labels,
        })
        this.agentVoiceMap = voices
        this.agentLabelOverrides = labels
      } catch (e) {
        /* assignment still works if voice map save fails */
      }
    },
    openWizard() {
      if (!this.canManage) return
      const fresh = DEFAULT_WIZARD()
      fresh.orgSystemPromptTemplate = this.wizard.orgSystemPromptTemplate
      fresh.firstMessage = this.wizard.firstMessage || DEFAULT_FIRST_MESSAGE
      fresh.promptFields = { ...this.wizard.promptFields }
      fresh.language = this.orgLanguage || ''
      fresh.voice = this.orgVoice || ''
      fresh.config = this.orgCallingSchema || null
      this.wizard = fresh
      this.wizardStep = 0
      this.createError = ''
      this.createdAgentId = ''
      this.setDefaultOnCreate = true
      this.showPromptPreview = false
      this.viewMode = 'create'
    },
    cancelWizard() {
      this.viewMode = 'list'
      this.createdAgentId = ''
      this.createError = ''
    },
    onWizardSubmit() {
      if (this.createdAgentId) return
      if (this.wizardStep < this.wizardSteps.length - 1) {
        if (this.wizardStep === 0 && !this.wizard.name.trim()) return
        this.wizardStep += 1
        return
      }
      this.createAgent()
    },
    buildCreatePayload() {
      // Pass wizard + org schema. createIndusAgent builds the POST body once
      // so language/voice stay on the fields ATS/Indus validate.
      return buildAdminCreateAgentInput({
        ...this.wizard,
        previewSystemPrompt: this.previewSystemPrompt,
        config: this.orgCallingSchema || this.wizard.config,
      })
    },
    async createAgent() {
      if (!this.canManage || !this.canSubmitWizard) return
      this.creating = true
      this.createError = ''
      try {
        const res = await aiCallingService.createIndusAgent(this.buildCreatePayload())
        const ok = res && (
          res.status === 'OK'
          || res.status === 200
          || res.status === 'success'
          || res.agent_id
          || res.agentId
          || res.data?.agent_id
        )
        if (ok) {
          this.createdAgentId = extractCreatedAgentId(res)

          this.$bvToast.toast(
            `${this.wizard.name || 'Agent'} created (${this.createdAgentId}). Language ${this.wizard.language} · ${this.wizard.voice}.`,
            {
              title: 'Agents',
              variant: 'success',
              solid: true,
              autoHideDelay: 8000,
            },
          )
          if (this.createdAgentId) {
            await this.persistCreatedAgentVoice(this.createdAgentId, {
              language: this.wizard.language,
              voice: this.wizard.voice,
              name: this.wizard.name,
            })
            if (!isBrokenHindiConfigAgent(this.createdAgentId)) {
              const preset = this.wizard.preset
              await this.assignEnglishAgentsToConfig({
                screeningId: preset === 'screening_en' ? this.createdAgentId : '',
                driveId: preset === 'drive_en' ? this.createdAgentId : '',
              })
            }
          }
          if (this.setDefaultOnCreate && this.createdAgentId) {
            await this.applyDefaultAgent(this.createdAgentId, { silent: true })
          }
          await this.loadAgents()
        } else if (isAiCallingNotDeployed(res)) {
          const msg = "Couldn't create the agent yet. Please try again later."
          this.createError = msg
          // eslint-disable-next-line no-console
          console.error('[AdminAgentsPanel] create agent not deployed', res)
          this.$bvToast.toast(msg, {
            title: 'Agents',
            variant: 'warning',
            solid: true,
            autoHideDelay: 8000,
          })
        } else {
          const msg = getAiCallingErrorMessage(res, 'ai-calls/agents')
            || "Couldn't create the agent. Please try again."
          this.createError = msg
          // eslint-disable-next-line no-console
          console.error('[AdminAgentsPanel] create agent failed', {
            httpStatus: res?.httpStatus,
            message: msg,
            first_sent: res?.first_sent,
            sent: res?.sent,
          })
          this.$bvToast.toast(msg, {
            title: 'Agents',
            variant: 'danger',
            solid: true,
            autoHideDelay: 12000,
          })
        }
      } catch (e) {
        this.createError = (e && e.message) || "Couldn't create the agent. Please try again."
        this.$bvToast.toast(this.createError, {
          title: 'Agents',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.creating = false
      }
    },
    async applyDefaultAgent(agentId, opts = {}) {
      const id = (agentId || '').toString().trim()
      if (!id) return false
      let ok = false
      const dedicated = await aiCallingService.setDefaultAgent(id)
      if (dedicated && (dedicated.status === 'OK' || dedicated.status === 200 || dedicated.status === 'success')) {
        ok = true
      } else {
        try {
          const cfgRes = await adminConfigService.getAiCallingConfig()
          const existing = cfgRes && (cfgRes.data || cfgRes)
          const base = existing && typeof existing === 'object' && !Array.isArray(existing.data)
            ? (existing.data || existing)
            : {}
          const saveRes = await adminConfigService.saveAiCallingConfig({
            ...base,
            agent_id: id,
            agentId: id,
          })
          ok = !!(saveRes && (saveRes.status === 'OK' || saveRes.status === 200 || saveRes.status === 'success'))
        } catch (e) {
          ok = false
        }
      }
      if (ok) {
        this.defaultAgentId = id
        if (!opts.silent) {
          const match = this.agents.find(a => String(a.agent_id) === id)
          const label = match ? this.humanAgentName(match) : id
          this.$bvToast.toast(`Default agent set to ${label}.`, {
            title: 'Agents',
            variant: 'success',
            solid: true,
          })
        }
      } else if (!opts.silent) {
        this.$bvToast.toast('Could not set the default agent. Assign it on the AI Calling tab instead.', {
          title: 'Agents',
          variant: 'warning',
          solid: true,
        })
      }
      return ok
    },
    async setAsDefault(agent) {
      this.settingDefault = agent.agent_id
      try {
        await this.applyDefaultAgent(agent.agent_id)
      } finally {
        this.settingDefault = ''
      }
    },
    confirmDelete(agent) {
      this.$bvModal.msgBoxConfirm(
        `Delete agent ${this.humanAgentName(agent)}? This cannot be undone.`,
        {
          title: 'Delete agent',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
        },
      ).then(confirmed => {
        if (confirmed) this.deleteAgent(agent.agent_id)
      })
    },
    async dropAgentFromList(agentId) {
      const id = normalizeIndusAgentId(agentId)
      const key = id.toUpperCase()
      this.rememberDeletedAgent(id)
      this.agents = this.excludeDeletedAgents(
        (this.agents || []).filter(a => listedIndusAgentId(a).toUpperCase() !== key),
      )
      const same = (value) => normalizeIndusAgentId(value).toUpperCase() === key
      if (same(this.defaultAgentId)) this.defaultAgentId = ''
      if (same(this.assignScreeningId)) this.assignScreeningId = ''
      if (same(this.assignDriveId)) this.assignDriveId = ''
      if (same(this.assignedScreeningId)) this.assignedScreeningId = ''
      if (same(this.assignedDriveId)) this.assignedDriveId = ''
      if (same(this.assignedOtherId)) this.assignedOtherId = ''
      this.clearTombstonedSlots()
      await this.pruneMissingAssignments(this.agents, { forceSave: true })
      this.agents = this.excludeDeletedAgents(this.agents)
    },
    async deleteAgent(agentId) {
      const id = normalizeIndusAgentId(agentId)
      this.deletingId = id
      try {
        const res = await aiCallingService.deleteIndusAgent(id)
        if (!isIndusAgentDeleteSuccess(res, id)) {
          this.$bvToast.toast(
            getAiCallingErrorMessage(res, `ai-calls/agents/${id}`) || 'Could not delete that agent.',
            { title: 'Agents', variant: 'danger', solid: true },
          )
          return
        }
        await this.dropAgentFromList(id)
        const stillListed = this.visibleAgents.some(
          a => listedIndusAgentId(a).toUpperCase() === id.toUpperCase(),
        )
        if (stillListed) {
          this.$bvToast.toast(
            'Indus removed the agent, but it is still on this list. Refresh and try again.',
            { title: 'Agents', variant: 'warning', solid: true },
          )
          return
        }
        const gone = isAgentAlreadyGone(res, `ai-calls/agents/${id}`)
        this.$bvToast.toast(
          gone
            ? 'That agent is already gone on Indus. Removed from this list.'
            : 'Agent deleted.',
          { title: 'Agents', variant: 'success', solid: true },
        )
      } catch (e) {
        this.$bvToast.toast(
          (e && e.message) || 'Could not delete that agent.',
          { title: 'Agents', variant: 'danger', solid: true },
        )
      } finally {
        this.deletingId = ''
      }
    },
    async finishWizard() {
      if (this.setDefaultOnCreate && this.createdAgentId && this.defaultAgentId !== this.createdAgentId) {
        this.savingDefault = true
        try {
          await this.applyDefaultAgent(this.createdAgentId)
        } finally {
          this.savingDefault = false
        }
      }
      this.viewMode = 'list'
      this.createdAgentId = ''
    },
  },
}
</script>

<style scoped>
.admin-agents-panel {
  --ai-brand: #00ac99;
  --ai-brand-soft: rgba(0, 172, 153, 0.12);
  --ai-brand-border: rgba(0, 172, 153, 0.4);
}
.admin-agents-panel__title {
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.3;
  color: #5e5873;
}
.admin-agents-panel__intro {
  max-width: 36rem;
}
.admin-agents-panel__search {
  max-width: 16rem;
  flex: 1 1 10rem;
}
.admin-agents-panel__list-wrap {
  min-height: 6rem;
}
.admin-agents-panel ::v-deep .btn-primary {
  background-color: var(--ai-brand);
  border-color: var(--ai-brand);
}
.admin-agents-panel ::v-deep .btn-primary:hover,
.admin-agents-panel ::v-deep .btn-primary:focus {
  background-color: #009688;
  border-color: #009688;
}
.admin-agents-panel ::v-deep .btn-outline-primary {
  color: var(--ai-brand);
  border-color: var(--ai-brand);
}
.admin-agents-panel ::v-deep .btn-outline-primary:hover,
.admin-agents-panel ::v-deep .btn-outline-primary:focus {
  background-color: var(--ai-brand);
  border-color: var(--ai-brand);
  color: #fff;
}
.admin-agents-panel ::v-deep .agent-wizard-tabs .nav-link {
  padding: 0.35rem 0.85rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #6e6b7b;
}
.admin-agents-panel ::v-deep .agent-wizard-tabs .nav-link.active {
  background-color: var(--ai-brand);
  border-color: var(--ai-brand);
  color: #fff;
}
.admin-agents-panel ::v-deep .admin-agents-progress .progress-bar {
  background-color: var(--ai-brand);
}
.admin-agents-panel ::v-deep .agent-voice-select {
  min-width: 200px;
}
.admin-agents-panel ::v-deep .agent-voice-select .vs__dropdown-toggle {
  min-height: 38px;
  padding: 0 8px;
  border-color: #d8d6de;
}
.admin-agents-panel ::v-deep .agent-voice-select.vs--open .vs__dropdown-toggle,
.admin-agents-panel ::v-deep .agent-voice-select .vs__dropdown-toggle:hover {
  border-color: var(--ai-brand);
}
.admin-agents-link,
.admin-agents-panel ::v-deep .admin-agents-link {
  color: var(--ai-brand);
}
.admin-agents-brand-icon {
  color: var(--ai-brand);
}
.admin-agents-default-note {
  background: var(--ai-brand-soft);
  border: 1px solid var(--ai-brand-border);
  color: #5e5873;
}
.admin-agents-default-badge {
  background: var(--ai-brand-soft);
  color: #007a6e;
  border: 1px solid var(--ai-brand-border);
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.15rem 0.4rem;
}
.admin-agents-role-badge {
  background: #f3f2f7;
  color: #6e6b7b;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.45rem;
}
.admin-agents-role-badge--screening {
  background: var(--ai-brand-soft);
  color: #007a6e;
}
.admin-agents-role-badge--drive {
  background: rgba(255, 159, 67, 0.14);
  color: #c2771e;
}
.admin-agent-card {
  border: 1px solid #e5e3ea;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.admin-agent-card--default {
  border-color: var(--ai-brand);
  box-shadow: 0 0 0 1px var(--ai-brand);
}
.admin-agent-card__name {
  font-size: 0.975rem;
  font-weight: 600;
  line-height: 1.3;
  color: #5e5873;
}
.admin-agent-card__code {
  font-size: 0.72rem;
  line-height: 1.3;
  letter-spacing: 0.01em;
  color: #b9b9c3;
  margin-top: 0.15rem;
}
.admin-agent-card__voice {
  font-size: 0.72rem;
  line-height: 1.3;
  margin-top: 0.2rem;
}
.admin-agents-empty {
  border: 1px dashed var(--ai-brand-border);
  background: var(--ai-brand-soft);
  color: #5e5873;
}
.admin-agents-review {
  border-color: #ebe9f1;
}
.agent-prompt-textarea,
.agent-preview-pre {
  font-size: 0.875rem;
  line-height: 1.45;
}
.agent-preview-pre {
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  max-height: 200px;
  overflow: auto;
}
.agent-review-dl dt,
.agent-review-dl dd {
  margin: 0;
}
.agent-wizard-footer {
  position: sticky;
  bottom: 0;
  background: var(--white, #fff);
  z-index: 2;
}
</style>
